import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { getUser } from "@/lib/api";
import { Plus, Trash2, Save, Loader2, Database } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function PricingConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const currentUser = getUser();
  const isAdmin = currentUser?.role === "admin";

  const load = async () => {
    const { data } = await api.get("/pricing/config");
    setCfg(data);
  };
  useEffect(() => { load(); }, []);

  const updateRowLocal = (id, patch) => {
    setCfg((c) => ({
      ...c,
      rows: c.rows.map((r) => {
        if (r.id !== id) return r;
        const nr = { ...r, ...patch };
        // recompute derived fields locally for immediate UI feedback
        const wh = (Number(nr.days_per_week) || 0) * (Number(nr.hours_per_day) || 0);
        nr.weekly_hours = +wh.toFixed(2);
        nr.monthly_hours = +(wh * 4.33).toFixed(2);
        nr.weekly_value = +(wh * (Number(nr.hourly_price) || 0)).toFixed(2);
        nr.monthly_avg = +(nr.weekly_value * 4.33).toFixed(2);
        return nr;
      }),
    }));
  };

  const saveRow = async (row) => {
    setBusyRow(row.id);
    try {
      await api.post("/pricing/config/rows", {
        id: row.id, name: row.name, days_per_week: row.days_per_week,
        hours_per_day: row.hours_per_day, hourly_price: row.hourly_price,
        active: row.active, order: row.order,
      });
      toast.success(`${row.name} guardado`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusyRow(null); }
  };

  const addRow = async () => {
    try {
      await api.post("/pricing/config/rows", {
        name: "NOVA LINHA", days_per_week: 5, hours_per_day: 8,
        hourly_price: 0, active: true,
        order: (cfg.rows.length + 1) * 10,
      });
      load();
    } catch (e) { toast.error("Erro"); }
  };

  const deleteRow = async (id) => {
    if (!window.confirm("Eliminar esta linha?")) return;
    await api.delete(`/pricing/config/rows/${id}`);
    load();
  };

  const saveTerms = async () => {
    setSaving(true);
    try {
      await api.put("/pricing/config", {
        commercial_terms_continuidade: cfg.commercial_terms_continuidade || "",
        commercial_terms_pontual: cfg.commercial_terms_pontual || "",
      });
      toast.success("Condições comerciais guardadas");
    } catch (e) { toast.error("Erro"); }
    finally { setSaving(false); }
  };

  // ---------- MULTIPLIERS ----------
  const [busyMult, setBusyMult] = useState(null);
  const updateMultLocal = (id, patch) => {
    setCfg((c) => ({
      ...c,
      multipliers: (c.multipliers || []).map((m) => m.id === id ? { ...m, ...patch } : m),
    }));
  };
  const saveMult = async (m) => {
    setBusyMult(m.id);
    try {
      await api.patch(`/pricing/multipliers/${m.id}`, {
        id: m.id, name: m.name, percentage: m.percentage, active: m.active,
        order: m.order, include_by_default: m.include_by_default, note: m.note,
      });
      toast.success(`Factor "${m.name}" guardado`);
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusyMult(null); }
  };
  const addMult = async () => {
    try {
      await api.post("/pricing/multipliers", {
        name: "Novo factor", percentage: 10, active: true,
        order: ((cfg.multipliers || []).length + 1) * 10,
        include_by_default: false, note: "",
      });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };
  const deleteMult = async (id) => {
    if (!window.confirm("Eliminar este factor multiplicador?")) return;
    try {
      await api.delete(`/pricing/multipliers/${id}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  if (!cfg) return <div className="p-10 text-muted-foreground">A carregar...</div>;

  const bulkLoadCatalog2026 = async () => {
    if (!window.confirm(
      "Vai substituir TODAS as linhas do catálogo pelas 39 do catálogo 2026 " +
      "(SEMANA, FDS, 30 DIAS, NOITES ATIVAS, NOITES CALMAS). " +
      "Marca automaticamente 3 defaults (SEMANA 8h, FDS 8h, 30 DIAS 8h). " +
      "Se tiveres linhas customizadas fora deste catálogo, PERDES-AS. Continuar?"
    )) return;
    setBulkLoading(true);
    try {
      const { data } = await api.post("/pricing/bulk-load-catalog-2026");
      toast.success(`Catálogo 2026 carregado — ${data.rows_loaded} linhas (${data.defaults} defaults)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao carregar catálogo");
    } finally {
      setBulkLoading(false);
    }
  };

  const inp = "bg-card border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-[#3B82F6]/30";

  return (
    <div className="p-8 lg:p-12 max-w-6xl mx-auto space-y-8" data-testid="pricing-config-root">
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground font-semibold mb-2">Configuração</div>
        <h1 className="font-display text-4xl lg:text-5xl tracking-tight text-foreground">Tabela de Preços</h1>
        <p className="text-muted-foreground text-sm mt-2">Preços aplicados automaticamente na estimativa. Valor Semana = P.Venda/H × Horas Semana. Média Mensal = Valor Semana × 4,33.</p>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-ui text-lg">Serviços</h3>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                data-testid="btn-bulk-load-2026"
                onClick={bulkLoadCatalog2026}
                disabled={bulkLoading}
                title="Substitui o catálogo pelos 39 preços 2026 (só admin)"
                className="inline-flex items-center gap-1.5 text-xs bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground rounded-full px-3 py-1.5 font-heading font-semibold tracking-tight"
              >
                {bulkLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />}
                Carregar catálogo 2026
              </button>
            )}
            <button data-testid="btn-add-row" onClick={addRow} className="inline-flex items-center gap-1.5 text-xs bg-muted hover:bg-muted border border-border rounded-full px-3 py-1.5">
              <Plus className="w-3.5 h-3.5" /> Adicionar linha
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                <th className="text-left py-3 px-2">Serviço</th>
                <th className="text-right py-3 px-2 w-20">Dias/Sem</th>
                <th className="text-right py-3 px-2 w-20">H/Dia</th>
                <th className="text-right py-3 px-2 w-20">H/Sem</th>
                <th className="text-right py-3 px-2 w-20">H/Mês</th>
                <th className="text-right py-3 px-2 w-24">P.Venda/H</th>
                <th className="text-right py-3 px-2 w-28">Valor Semana</th>
                <th className="text-right py-3 px-2 w-28">Média Mensal</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {cfg.rows.map((r) => (
                <tr key={r.id} className="border-b border-border/60" data-testid={`row-${r.id}`}>
                  <td className="py-2 px-2">
                    <input className={`${inp} w-full`} value={r.name} onChange={(e) => updateRowLocal(r.id, { name: e.target.value })} data-testid={`row-name-${r.id}`} />
                  </td>
                  <td className="py-2 px-2">
                    <input type="number" step="0.5" className={`${inp} w-full text-right`} value={r.days_per_week} onChange={(e) => updateRowLocal(r.id, { days_per_week: parseFloat(e.target.value) || 0 })} data-testid={`row-days-${r.id}`} />
                  </td>
                  <td className="py-2 px-2">
                    <input type="number" step="0.5" className={`${inp} w-full text-right`} value={r.hours_per_day} onChange={(e) => updateRowLocal(r.id, { hours_per_day: parseFloat(e.target.value) || 0 })} data-testid={`row-hours-${r.id}`} />
                  </td>
                  <td className="py-2 px-2 text-right text-xs text-muted-foreground font-mono">{fmt(r.weekly_hours)}</td>
                  <td className="py-2 px-2 text-right text-xs text-muted-foreground font-mono">{fmt(r.monthly_hours)}</td>
                  <td className="py-2 px-2">
                    <input type="number" step="0.01" className={`${inp} w-full text-right`} value={r.hourly_price} onChange={(e) => updateRowLocal(r.id, { hourly_price: parseFloat(e.target.value) || 0 })} data-testid={`row-price-${r.id}`} />
                  </td>
                  <td className="py-2 px-2 text-right text-xs text-muted-foreground font-mono">{fmt(r.weekly_value)} €</td>
                  <td className="py-2 px-2 text-right text-sm text-foreground font-semibold font-mono">{fmt(r.monthly_avg)} €</td>
                  <td className="py-2 px-2 text-right space-x-1">
                    <button onClick={() => saveRow(r)} disabled={busyRow === r.id} className="text-foreground hover:text-foreground p-1" title="Guardar linha" data-testid={`save-row-${r.id}`}>
                      {busyRow === r.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </button>
                    <button onClick={() => deleteRow(r.id)} className="text-muted-foreground/70 hover:text-rose-500 p-1" title="Eliminar" data-testid={`del-row-${r.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {cfg.rows.length === 0 && (
                <tr><td colSpan={9} className="text-center py-6 text-sm text-muted-foreground/70">Sem linhas — clique em "Adicionar linha".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-4" data-testid="multipliers-section">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-ui text-lg">Factores multiplicadores</h3>
            <p className="text-xs text-muted-foreground mt-1">
              Percentagens que podem ser aplicadas às estimativas (nocturno, feriado, etc.).
              Cada estimativa pode remover factores individualmente — não afecta o catálogo.
            </p>
          </div>
          <button
            data-testid="btn-add-multiplier-row"
            onClick={addMult}
            className="inline-flex items-center gap-1.5 text-xs bg-[hsl(var(--warning-bg))] hover:bg-[hsl(var(--warning-bg))] border border-[hsl(var(--warning))]/40 text-[hsl(var(--warning))] rounded-full px-3 py-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Adicionar factor
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold border-b border-border">
                <th className="text-left py-3 px-2">Nome</th>
                <th className="text-right py-3 px-2 w-24">%</th>
                <th className="text-left py-3 px-2">Nota</th>
                <th className="text-center py-3 px-2 w-20">Activo</th>
                <th className="text-center py-3 px-2 w-24">Auto-add</th>
                <th className="text-right py-3 px-2 w-20">Ordem</th>
                <th className="w-24"></th>
              </tr>
            </thead>
            <tbody>
              {(cfg.multipliers || []).map((m) => (
                <tr key={m.id} className="border-b border-border/60" data-testid={`mult-row-config-${m.id}`}>
                  <td className="py-2 px-2">
                    <input className={`${inp} w-full`} value={m.name || ""} onChange={(e) => updateMultLocal(m.id, { name: e.target.value })} data-testid={`mult-name-${m.id}`} />
                  </td>
                  <td className="py-2 px-2">
                    <input type="number" step="0.5" className={`${inp} w-full text-right`} value={m.percentage || 0} onChange={(e) => updateMultLocal(m.id, { percentage: parseFloat(e.target.value) || 0 })} data-testid={`mult-pct-${m.id}`} />
                  </td>
                  <td className="py-2 px-2">
                    <input className={`${inp} w-full`} value={m.note || ""} placeholder="Descrição opcional" onChange={(e) => updateMultLocal(m.id, { note: e.target.value })} data-testid={`mult-note-${m.id}`} />
                  </td>
                  <td className="py-2 px-2 text-center">
                    <input type="checkbox" checked={m.active !== false} onChange={(e) => updateMultLocal(m.id, { active: e.target.checked })} data-testid={`mult-active-${m.id}`} />
                  </td>
                  <td className="py-2 px-2 text-center">
                    <input type="checkbox" checked={!!m.include_by_default} onChange={(e) => updateMultLocal(m.id, { include_by_default: e.target.checked })} data-testid={`mult-default-${m.id}`} title="Adicionar automaticamente a cada nova estimativa" />
                  </td>
                  <td className="py-2 px-2">
                    <input type="number" className={`${inp} w-full text-right`} value={m.order || 0} onChange={(e) => updateMultLocal(m.id, { order: parseInt(e.target.value) || 0 })} data-testid={`mult-order-${m.id}`} />
                  </td>
                  <td className="py-2 px-2 text-right space-x-1">
                    <button onClick={() => saveMult(m)} disabled={busyMult === m.id} className="text-foreground hover:text-foreground p-1" title="Guardar" data-testid={`save-mult-${m.id}`}>
                      {busyMult === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    </button>
                    <button onClick={() => deleteMult(m.id)} className="text-muted-foreground/70 hover:text-rose-500 p-1" title="Eliminar" data-testid={`del-mult-config-${m.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {(cfg.multipliers || []).length === 0 && (
                <tr><td colSpan={7} className="text-center py-6 text-sm text-muted-foreground/70">Sem factores — clique em "Adicionar factor".</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="bg-card border border-border rounded-2xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="font-ui text-lg">Condições comerciais</h3>
          <button data-testid="save-terms" onClick={saveTerms} disabled={saving} className="inline-flex items-center gap-1.5 text-xs bg-foreground hover:bg-slate-800 text-background rounded-full px-3 py-1.5">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar
          </button>
        </div>
        <p className="text-xs text-muted-foreground">Estes textos são inseridos automaticamente na estimativa consoante o tipo de serviço escolhido na Qualificação Inicial (campo "Continuidade"). Use <code className="bg-muted px-1 py-0.5 rounded font-mono text-[10px]">{"{{total_value}}"}</code> no texto pontual para inserir o valor total da estimativa (ex.: <i>"é de {"{{total_value}}"} €"</i> → <i>"é de 577,00 €"</i>). Alterações só afetam <b>novas</b> estimativas — leads existentes mantêm o texto guardado até serem regeneradas.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Continuidade (serviço contínuo)</div>
            <textarea rows={7} data-testid="terms-continuidade" className={`${inp} w-full`} value={cfg.commercial_terms_continuidade || ""} onChange={(e) => setCfg({ ...cfg, commercial_terms_continuidade: e.target.value })} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-2">Pontual (serviço ocasional)</div>
            <textarea rows={7} data-testid="terms-pontual" className={`${inp} w-full`} value={cfg.commercial_terms_pontual || ""} onChange={(e) => setCfg({ ...cfg, commercial_terms_pontual: e.target.value })} />
          </div>
        </div>
      </div>
    </div>
  );
}
